// src/pages/dealerWarehouse/hooks/useDealerWarehouseForm.js
import { useMemo, useState } from "react";
import { useLanguage } from "../../../contexts/LanguageContext";
import {
  useGetWarehouseTypesQuery,
  useGetParentWarehousesQuery
} from "../../../api/endpoints/dealerWarehouse";
import { useSelector } from "react-redux";
import { selectUserParams } from "../../../store/slices/userParamsSlice";
import { useGetUserParamsQuery } from "../../../api/endpoints/user";

/**
 * Modal (Create/Update) form state & işlemleri
 */
const useDealerWarehouseForm = (saveWarehouse) => {
  const { th, getCurrentLanguageId } = useLanguage();

  // UserParams → appBranchId
  const { data: userParamsResp } = useGetUserParamsQuery();
  const userParamsFromStore = useSelector(selectUserParams);
  const userParams = userParamsFromStore || userParamsResp || {};
  const appBranchId = userParams?.dealerId ?? 0;

  const languageId = getCurrentLanguageId();

  const ready = Boolean(appBranchId && languageId);

  // dialog & mode
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentWarehouse, setCurrentWarehouse] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // form alanları
  const [warehouseName, setWarehouseName] = useState("");
  const [relatedWarehouseId, setRelatedWarehouseId] = useState(null);
  const [warehouseTypeId, setWarehouseTypeId] = useState(null);
  const [warehouseStyle, setWarehouseStyle] = useState(1); // 1 sabit, 2 gezici
  const [isActive, setIsActive] = useState(true);

  const { data: typeItems = [] } = useGetWarehouseTypesQuery(
    { languageId },
    { skip: !languageId, refetchOnMountOrArgChange: false }
  );

  const { data: parentItems = [] } = useGetParentWarehousesQuery(
    { appBranchId, languageId, pageNumber: 1, pageSize: 1000, isActive: 1 },
    { skip: !ready, refetchOnMountOrArgChange: false }
  );

  const typeOptions = useMemo(
    () =>
      (typeItems?.data || typeItems || []).map((x) => ({
        label: x.translatedText || x.text || x.label,
        value: Number(x.code ?? x.value ?? x.id)
      })),
    [typeItems]
  );

  const parentOptions = useMemo(
    () =>
      (parentItems?.data || parentItems || []).map((x) => ({
        label: x.warehouseName || x.text || x.label,
        value: x.id
      })),
    [parentItems]
  );

  const resetForm = () => {
    setWarehouseName("");
    setRelatedWarehouseId(null);
    setWarehouseTypeId(null);
    setWarehouseStyle(1);
    setIsActive(true);
  };

  const setFormData = (w) => {
    setWarehouseName(w.warehouseName || "");
    setRelatedWarehouseId(w.relatedWarehouseId ?? null);
    setWarehouseTypeId(w.warehouseTypeId ?? null);
    setWarehouseStyle(w.warehouseStyle ?? 1);
    setIsActive((w.isActive ?? 1) === 1);
  };

  const handleAddNew = () => {
    setEditMode(false);
    setCurrentWarehouse(null);
    resetForm();
    setSubmitted(false);
    setDialogVisible(true);
  };

  const handleEdit = (warehouse) => {
    setEditMode(true);
    setCurrentWarehouse(warehouse);
    setFormData(warehouse);
    setSubmitted(false);
    setDialogVisible(true);
  };

  const hideDialog = () => setDialogVisible(false);

  const handleSubmit = async () => {
    setSubmitted(true);

    if (warehouseName.trim() && warehouseTypeId) {
      const payload = {
        id: editMode ? currentWarehouse?.id : undefined,
        appBranchId,            
        languageId,             
        warehouseName,
        relatedWarehouseId: relatedWarehouseId || null,
        warehouseTypeId: Number(warehouseTypeId),
        warehouseStyle: Number(warehouseStyle),
        isActive: isActive ? 1 : 0
      };

      const ok = await saveWarehouse(payload, editMode, currentWarehouse);
      if (ok) hideDialog();
      return ok;
    }
    return false;
  };

  const renderDialogFooter = () => (
    <div className="flex justify-content-end gap-2">
      <button className="p-button p-component p-button-outlined" type="button" onClick={hideDialog}>
        <span className="p-button-icon p-button-icon-left pi pi-times"></span>
        <span className="p-button-label">{th("common.cancel")}</span>
      </button>
      <button className="p-button p-component" onClick={handleSubmit}>
        <span className="p-button-icon p-button-icon-left pi pi-check"></span>
        <span className="p-button-label">{th("common.save")}</span>
      </button>
    </div>
  );

  return {
    // dialog
    dialogVisible,
    editMode,
    submitted,
    hideDialog,
    handleAddNew,
    handleEdit,
    handleSubmit,
    renderDialogFooter,

    // fields
    warehouseName,
    relatedWarehouseId,
    warehouseTypeId,
    warehouseStyle,
    isActive,
    setWarehouseName,
    setRelatedWarehouseId,
    setWarehouseTypeId,
    setWarehouseStyle,
    setIsActive,

    // lookups
    typeOptions,
    parentOptions
  };
};

export default useDealerWarehouseForm;
